import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { fetchStockSuggestions } from "../../utils/StockApiUtils";

const StockAutocompleteInput = ({
    value,
    onValueChange,
    onSubmit,
    suggestionFilters = {},
    placeholder = "Search products",
    label,
    disabled = false,
    size = "small"
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const suggestTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (suggestTimerRef.current) {
                clearTimeout(suggestTimerRef.current);
            }
        };
    }, []);

    const scheduleSuggestions = (nextValue) => {
        if (suggestTimerRef.current) {
            clearTimeout(suggestTimerRef.current);
        }

        if (!nextValue || nextValue.length < 2) {
            setSuggestions([]);
            setIsSuggesting(false);
            return;
        }

        suggestTimerRef.current = setTimeout(async () => {
            setIsSuggesting(true);
            try {
                const results = await fetchStockSuggestions({
                    query: nextValue,
                    limit: 6,
                    filters: suggestionFilters
                });
                setSuggestions(results);
            } catch (error) {
                console.error("Error fetching stock suggestions:", error);
                setSuggestions([]);
            } finally {
                setIsSuggesting(false);
            }
        }, 300);
    };

    const handleSubmit = (nextValue) => {
        if (!onSubmit) {
            return;
        }
        const trimmed = (nextValue || "").trim();
        onSubmit(trimmed);
    };

    return (
        <Autocomplete
            fullWidth
            freeSolo
            disablePortal
            options={suggestions}
            loading={isSuggesting}
            filterOptions={(options) => options}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
            inputValue={value}
            onInputChange={(event, newInputValue) => {
                onValueChange(newInputValue);
                scheduleSuggestions(newInputValue);
            }}
            onChange={(event, newValue) => {
                const nextValue = typeof newValue === "string" ? newValue : newValue?.name || "";
                if (!nextValue) {
                    return;
                }
                onValueChange(nextValue);
                setSuggestions([]);
                if (suggestTimerRef.current) {
                    clearTimeout(suggestTimerRef.current);
                }
                handleSubmit(nextValue);
            }}
            renderOption={(props, option) => (
                <li {...props} key={option.sku}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {(option.manufacturer ? `${option.manufacturer} • ` : "")}SKU {option.sku}
                        </Typography>
                    </Box>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    size={size}
                    label={label}
                    placeholder={placeholder}
                    disabled={disabled}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            handleSubmit(value);
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <>
                                {isSuggesting ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
};

export default StockAutocompleteInput;
